import { SessionDto } from '@data-access/bulk-operations-api';

/**
 * Extracts the version from the current URL path.
 */
export function getVersionFromUrl(): string | undefined {
  return getPathParts(window.location.pathname).version;
}

/**
 * Extracts the organization ID from the current URL path.
 *
 * @returns {string|undefined} The organization ID extracted from the URL path.
 */
export function getOrgIdFromUrl(): string | undefined {
  return getPathParts(window.location.pathname).orgId;
}

/**
 * Extracts organization ID from OAuth state parameter.
 * State format: "<nonce>;<path>"
 */
function getOrgIdFromState(): string | undefined {
  const urlParams = new URLSearchParams(window.location.search);
  const stateParam = urlParams.get('state');

  if (!stateParam) {
    return undefined;
  }

  const decodedState = decodeURIComponent(stateParam);

  // Split on separator to isolate path portion from nonce
  const separatorIndex = decodedState.indexOf(';');
  if (separatorIndex === -1) {
    return undefined;
  }

  const path = decodedState.substring(separatorIndex + 1);
  return getPathParts(path).orgId;
}

/**
 * Valid path examples:
 *   /
 *   /<navigationPath>
 *   /<orgId>
 *   /<orgId>/
 *   /<orgId>/<navigationPath>
 *   /v/<version>
 *   /v/<version>/<navigationPath>
 *   /v/<version>/<orgId>
 *   /v/<version>/<orgId>/
 *   /v/<version>/<orgId>/<navigationPath>
 */
function getPathParts(path: string): { version?: string; orgId?: string; navigationPath?: string } {
  const match = /^(?:[/]v[/]([^/]+))?(?:[/]([1-9][0-9]*))?(?:$|[/]$|[/](.+)$)/.exec(path);
  return {
    version: match?.at(1),
    orgId: match?.at(2),
    navigationPath: match?.at(3),
  };
}

/**
 * Extracts organization ID from contextSelector:switchContext query parameter.
 */
function getSwitchContextId(): string | undefined {
  const urlParams = new URLSearchParams(window.location.search);
  const switchContextId = urlParams.get('contextSelector:switchContext');

  if (switchContextId && /^[1-9][0-9]*$/.test(switchContextId)) {
    return switchContextId;
  }

  return undefined;
}

/**
 * Called before initializing app to determine which organization ID to use.
 * Determines the preferred organization ID based URL parameters and user session.
 *
 * @param {SessionDto} [session] - The current session data.
 * @returns {string} The preferred organization ID.
 */
export function getPreferredOdpOrgId(session?: SessionDto): string {
  const switchContextId = getSwitchContextId();
  const urlPathParam = getOrgIdFromUrl();
  const orgIdFromState = getOrgIdFromState();

  let companyId = '';

  if (switchContextId) {
    companyId = switchContextId;
  } else if (urlPathParam) {
    companyId = urlPathParam;
  } else if (orgIdFromState) {
    companyId = orgIdFromState;
  } else if (session?.lastUsedContextId) {
    companyId = session.lastUsedContextId.toString();
  } else if (session?.availableContexts?.[0]?.id) {
    companyId = session.availableContexts[0].id.toString();
  }

  return companyId;
}

export function getPathWithPreferredOrgId(session?: SessionDto): string {
  const orgId = getPreferredOdpOrgId(session);
  const parts = getPathParts(window.location.pathname);

  let path = '';
  path += parts.version ? '/v/' + parts.version : '';
  path += orgId ? '/' + orgId : '';
  path += parts.navigationPath ? '/' + parts.navigationPath : '';
  return path;
}
